import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CalendarRange, GitBranch, Info, Save } from "lucide-react";
import AdminHeader from "@/components/admin/AdminHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatSvDateTime } from "@/utils/date";
import {
  bulkSetSeasonRegularSeason,
  getAdminOfficialMatchesStructure,
  updateAdminOfficialMatchStructure,
} from "@/api/adminMatches";

const STAGE_TYPE_OPTIONS = [
  { value: "__none__", label: "Ingen klassning" },
  { value: "regular_season", label: "Grundserie" },
  { value: "quarterfinal_series", label: "Kvartsfinalserie" },
  { value: "semifinal_series", label: "Semifinalserie" },
  { value: "final", label: "Final" },
];

const CLASSIFICATION_OPTIONS = [
  { value: "all", label: "Alla" },
  { value: "unclassified", label: "Oklassade" },
  { value: "regular", label: "Grundserie" },
  { value: "playoff", label: "Slutspel" },
];

const STAGE_FILTER_OPTIONS = [
  { value: "all", label: "Alla faser" },
  { value: "regular_season", label: "Grundserie" },
  { value: "quarterfinal_series", label: "Kvartsfinalserie" },
  { value: "semifinal_series", label: "Semifinalserie" },
  { value: "final", label: "Final" },
];

const HINT_OPTIONS = [
  { value: "all", label: "Alla heuristiker" },
  { value: "regular_season_candidate", label: "Kandidat: grundserie" },
  { value: "playoff_candidate", label: "Kandidat: slutspel" },
  { value: "uncertain", label: "Osäker" },
];

const STAGE_GROUP_OPTIONS = {
  quarterfinal_series: ["QF1", "QF2", "QF3"],
  semifinal_series: ["SF1", "SF2"],
  final: ["F1"],
};

function getStageTypeLabel(value) {
  return STAGE_TYPE_OPTIONS.find((option) => option.value === value)?.label || "Ingen klassning";
}

function getStageGroupValue(item, draft) {
  if (draft?.stage_group !== undefined) return draft.stage_group || "__none__";
  return item?.stage_group || "__none__";
}

function getLegValue(item, draft) {
  if (draft?.leg !== undefined) return draft.leg == null ? "__none__" : String(draft.leg);
  return item?.leg == null ? "__none__" : String(item.leg);
}

function normalizeDraft(item, draft) {
  const stageType = draft?.stage_type !== undefined ? draft.stage_type : (item?.stage_type || "__none__");
  const stageGroup = draft?.stage_group !== undefined ? draft.stage_group : (item?.stage_group || "__none__");
  const leg = draft?.leg !== undefined ? draft.leg : (item?.leg == null ? "__none__" : String(item.leg));

  return {
    stage_type: stageType === "__none__" ? null : stageType,
    stage_group: stageGroup === "__none__" ? null : stageGroup,
    leg: leg === "__none__" ? null : Number(leg),
  };
}

function validateDraft(item, draft) {
  const normalized = normalizeDraft(item, draft);
  const stageType = normalized.stage_type;
  const stageGroup = normalized.stage_group;
  const leg = normalized.leg;

  if (!stageType) {
    if (stageGroup || leg != null) return "stage_group och leg kräver att stage_type är satt.";
    return "";
  }
  if (stageType === "regular_season") {
    if (stageGroup || leg != null) return "Grundserie får inte ha stage_group eller leg.";
    return "";
  }

  const allowedGroups = STAGE_GROUP_OPTIONS[stageType] || [];
  if (!stageGroup || !allowedGroups.includes(stageGroup)) {
    return "Välj rätt block för slutspelsmatchen.";
  }
  if (leg !== 1 && leg !== 2) {
    return "Välj möte 1 eller 2 för slutspelsmatchen.";
  }
  return "";
}

function stageBadge(item) {
  if (!item?.stage_type) return { label: "Oklassad", variant: "secondary" };
  if (item.stage_type === "regular_season") return { label: "Grundserie", variant: "outline" };
  return { label: getStageTypeLabel(item.stage_type), variant: "default" };
}

function hintBadge(item) {
  switch (item?.classification_hint) {
    case "regular_season_candidate":
      return { label: "Kandidat: grundserie", className: "border-emerald-500/40 text-emerald-700 dark:text-emerald-300" };
    case "playoff_candidate":
      return { label: "Kandidat: slutspel", className: "border-amber-500/40 text-amber-700 dark:text-amber-300" };
    case "uncertain":
      return { label: "Osäker", className: "border-rose-500/40 text-rose-700 dark:text-rose-300" };
    default:
      return { label: "Ingen heuristik", className: "" };
  }
}

function stageTitle(stageType) {
  switch (stageType) {
    case "quarterfinal_series":
      return "Kvartsfinal-kandidat";
    case "semifinal_series":
      return "Semifinal-kandidat";
    case "final":
      return "Final-kandidat";
    default:
      return "Slutspelskandidat";
  }
}

function formatSeedLabel(positions = []) {
  const visible = positions.filter((value) => Number.isFinite(Number(value)));
  if (!visible.length) return null;
  return visible.map((value) => `#${value}`).join(" vs ");
}

const QUICK_ACTIONS = [
  ["QF1 • M1", { stage_type: "quarterfinal_series", stage_group: "QF1", leg: "1" }],
  ["QF1 • M2", { stage_type: "quarterfinal_series", stage_group: "QF1", leg: "2" }],
  ["QF2 • M1", { stage_type: "quarterfinal_series", stage_group: "QF2", leg: "1" }],
  ["QF2 • M2", { stage_type: "quarterfinal_series", stage_group: "QF2", leg: "2" }],
  ["QF3 • M1", { stage_type: "quarterfinal_series", stage_group: "QF3", leg: "1" }],
  ["QF3 • M2", { stage_type: "quarterfinal_series", stage_group: "QF3", leg: "2" }],
  ["SF1 • M1", { stage_type: "semifinal_series", stage_group: "SF1", leg: "1" }],
  ["SF1 • M2", { stage_type: "semifinal_series", stage_group: "SF1", leg: "2" }],
  ["SF2 • M1", { stage_type: "semifinal_series", stage_group: "SF2", leg: "1" }],
  ["SF2 • M2", { stage_type: "semifinal_series", stage_group: "SF2", leg: "2" }],
  ["Final • M1", { stage_type: "final", stage_group: "F1", leg: "1" }],
  ["Final • M2", { stage_type: "final", stage_group: "F1", leg: "2" }],
];

const MANUAL_MATCH_TEMPLATE = `db.official_matches.insertOne({
  id: "manual-2026-qf1-match1",
  season: 2026,
  date: ISODate("2026-08-18T17:00:00.000Z"),
  home_team: "Smederna",
  away_team: "Vargarna",
  status: "upcoming",
  source: "manual",
  source_match_id: null,
  used: false,
  createdAt: new Date()
})`;

export default function AdminMatchStructurePage() {
  const currentYear = new Date().getFullYear();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [season, setSeason] = useState(String(currentYear));
  const [classification, setClassification] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [hintFilter, setHintFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState({});

  const queryKey = ["adminOfficialMatchStructure", season, classification, stageFilter, hintFilter, search];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      getAdminOfficialMatchesStructure({
        season: Number(season),
        classification,
        stageType: stageFilter,
        hint: hintFilter,
        search,
      }),
    enabled: !!season && isSuperAdmin,
    staleTime: 30_000,
  });

  const items = data?.items || [];
  const regularStandingsPreview = data?.heuristic_regular_standings || [];
  const heuristicPlayoffGroups = data?.heuristic_playoff_groups || [];

  const seasonOptions = useMemo(() => {
    const values = new Set([String(currentYear), "2026", "2025"]);
    items.forEach((item) => {
      if (item?.season != null) values.add(String(item.season));
    });
    return Array.from(values).sort((a, b) => Number(b) - Number(a));
  }, [currentYear, items]);

  const saveMutation = useMutation({
    mutationFn: ({ officialId, payload }) => updateAdminOfficialMatchStructure(officialId, payload),
    onSuccess: (result) => {
      const item = result?.item;
      if (item?.id) {
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[item.id];
          return next;
        });
      }
      qc.invalidateQueries({ queryKey });
      toast.success("Matchstruktur sparad.");
    },
    onError: (error) => {
      toast.error(error?.message || "Kunde inte spara matchstruktur");
    },
  });

  const bulkMutation = useMutation({
    mutationFn: (targetSeason) => bulkSetSeasonRegularSeason(Number(targetSeason)),
    onSuccess: (result) => {
      setDrafts({});
      qc.invalidateQueries({ queryKey });
      toast.success(`Satte ${result?.modified ?? 0} matcher till grundserie.`);
    },
    onError: (error) => {
      toast.error(error?.message || "Bulk-uppdatering misslyckades");
    },
  });

  const handleDraftChange = (officialId, patch) => {
    setDrafts((prev) => {
      const current = prev[officialId] || {};
      return {
        ...prev,
        [officialId]: { ...current, ...patch },
      };
    });
  };

  const handleStageTypeChange = (officialId, nextStageType) => {
    if (nextStageType === "regular_season" || nextStageType === "__none__") {
      handleDraftChange(officialId, {
        stage_type: nextStageType,
        stage_group: "__none__",
        leg: "__none__",
      });
      return;
    }

    const groups = STAGE_GROUP_OPTIONS[nextStageType] || [];
    handleDraftChange(officialId, {
      stage_type: nextStageType,
      stage_group: groups[0] || "__none__",
      leg: "1",
    });
  };

  const applyQuickAction = (officialId, patch) => {
    handleDraftChange(officialId, patch);
  };

  const handleSaveRow = (item) => {
    const draft = drafts[item.id];
    const message = validateDraft(item, draft);
    if (message) {
      toast.error(message);
      return;
    }
    saveMutation.mutate({
      officialId: item.id,
      payload: normalizeDraft(item, draft),
    });
  };

  const handleBulkRegular = () => {
    const ok = window.confirm(`Sätt alla matcher i säsong ${season} till grundserie och rensa playoff-fält?`);
    if (!ok) return;
    bulkMutation.mutate(season);
  };

  return (
    <div className="min-h-screen">
      <AdminHeader />
      <main className="container mx-auto px-4 py-6 space-y-6">
        {!isSuperAdmin ? (
          <Card>
            <CardHeader>
              <CardTitle>Behörighet saknas</CardTitle>
              <CardDescription>
                Matchstruktur kan bara hanteras av superadmin eftersom den styr tabell och slutspel.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-primary" />
              <CardTitle>Matchstruktur</CardTitle>
            </div>
            <CardDescription>
              Sätt rätt stage_type, stage_group och leg på officiella matcher. Det här styr tabell och slutspel.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="border border-sky-500/35 bg-sky-500/10 p-4 text-sm">
              <div className="flex items-start gap-3">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-sky-700 dark:text-sky-300" />
                <div className="min-w-0 space-y-3">
                  <div>
                    <div className="font-semibold text-foreground">Manuellt tillagda officiella matcher</div>
                    <p className="mt-1 text-muted-foreground">
                      En manuell match ska innehålla fälten i mallen nedan. Matchens interna
                      <code className="mx-1 font-mono text-foreground">id</code>
                      är permanent och
                      <code className="mx-1 font-mono text-foreground">source_match_id</code>
                      ska vara <code className="font-mono text-foreground">null</code> tills Flashcore publicerar matchen.
                    </p>
                  </div>

                  <ul className="grid list-disc gap-1 pl-5 text-muted-foreground">
                    <li>Lagnamnen ska motsvara lagregistret och hemma-/bortalag måste ligga i rätt ordning.</li>
                    <li>Datumet ska sparas som ett riktigt MongoDB-datum och status ska vara <code className="font-mono text-foreground">upcoming</code>.</li>
                    <li>Automatisk koppling kräver exakt säsong, exakt lagordning och exakt svenskt kalenderdatum. Ingen tolerans på dagar används.</li>
                    <li>Vid en unik träff behålls det manuella id:t och Flashcores id sparas i <code className="font-mono text-foreground">source_match_id</code>.</li>
                    <li>Resultat, laguppställning och slutspelsstruktur läggs inte in i grundmallen.</li>
                  </ul>

                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-sky-800 dark:text-sky-200">
                      MongoDB-mall
                    </div>
                    <pre className="overflow-x-auto border border-border/70 bg-background/80 p-3 font-mono text-xs leading-5 text-foreground">
                      <code>{MANUAL_MATCH_TEMPLATE}</code>
                    </pre>
                  </div>
                </div>
              </div>
            </div>

            {regularStandingsPreview.length > 0 && (
              <div className="grid gap-4 xl:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
                <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
                  <div className="text-sm font-medium">Grundserietabell</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    Samma beräkning som ordinarie tabellen, inklusive bonuspoäng och manuella bonusoverrides.
                  </div>
                  <div className="mt-4 space-y-2">
                    {regularStandingsPreview.slice(0, 6).map((row) => (
                      <div
                        key={row.team}
                        className="flex items-center justify-between rounded-lg border border-border/60 bg-background/80 px-3 py-2 text-sm"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">#{row.position}</Badge>
                          <span className="font-medium">{row.team}</span>
                        </div>
                        <span className="text-muted-foreground">{row.points} p</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
                  <div className="text-sm font-medium">Heuristiska slutspelspar</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    Bygger på samma 14-matchersregel som analys-scriptet. Visar vilka lagpar som ser ut att bilda slutspel.
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {heuristicPlayoffGroups.map((group, index) => (
                      <div key={`${group.stage_type}-${group.teams?.join("-")}-${index}`} className="rounded-lg border border-border/60 bg-background/80 p-4">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-semibold">{stageTitle(group.stage_type)}</div>
                            {group.stage_group ? <Badge variant="outline">{group.stage_group}</Badge> : null}
                            {group.stage_group_source === "explicit" ? (
                              <Badge variant="outline">Sparad struktur</Badge>
                            ) : group.stage_group_source === "seeded" ? (
                              <Badge variant="outline">Seedad från tabell</Badge>
                            ) : null}
                          </div>
                          <Badge variant="outline">{group.matches?.length || 0} matcher</Badge>
                        </div>
                        <div className="mt-3 text-sm font-medium">
                          {group.teams?.[0] || "Ej fastställd"} - {group.teams?.[1] || "Ej fastställd"}
                        </div>
                        {formatSeedLabel(group.seed_positions) ? (
                          <div className="mt-1 text-xs text-muted-foreground">
                            Seed: {formatSeedLabel(group.seed_positions)}
                          </div>
                        ) : null}
                        <div className="mt-3 space-y-2">
                          {(group.matches || []).map((match) => (
                            <div key={match.id} className="rounded border border-border/50 px-3 py-2 text-xs text-muted-foreground">
                              <div className="font-medium text-foreground">
                                {match.home_team} - {match.away_team}
                              </div>
                              <div>
                                {match.date ? formatSvDateTime(match.date, { dateStyle: "medium", timeStyle: "short" }) : "Saknar datum"}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-xl border border-border/70 bg-muted/30 p-4 text-sm">
              <div className="font-medium">Rekommenderat arbetsflöde</div>
              <div className="mt-1 text-muted-foreground">
                Börja med bulk för grundserie. Filtrera sedan på <span className="font-medium text-foreground">Kandidat: slutspel</span> och använd snabbknapparna på varje match för att sätta QF/SF/Final.
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-[140px_180px_200px_220px_1fr]">
              <Select value={season} onValueChange={setSeason}>
                <SelectTrigger>
                  <SelectValue placeholder="Säsong" />
                </SelectTrigger>
                <SelectContent className="bg-card border border-border shadow-lg">
                  {seasonOptions.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={classification} onValueChange={setClassification}>
                <SelectTrigger>
                  <SelectValue placeholder="Klassning" />
                </SelectTrigger>
                <SelectContent className="bg-card border border-border shadow-lg">
                  {CLASSIFICATION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Fas" />
                </SelectTrigger>
                <SelectContent className="bg-card border border-border shadow-lg">
                  {STAGE_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={hintFilter} onValueChange={setHintFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Heuristik" />
                </SelectTrigger>
                <SelectContent className="bg-card border border-border shadow-lg">
                  {HINT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Sök på lag eller match-id"
              />
            </div>

            <div className="flex flex-col gap-3 rounded-xl border border-border/70 bg-muted/30 p-4 md:flex-row md:items-center md:justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CalendarRange className="h-4 w-4 text-primary" />
                  Grundserie-bulk för vald säsong
                </div>
                <p className="text-sm text-muted-foreground">
                  Använd detta när hela grundserieschemat är importerat och du vill sätta alla matcher till regular_season.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleBulkRegular}
                disabled={bulkMutation.isPending}
              >
                Sätt alla i {season} till grundserie
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {isLoading ? "Laddar matcher..." : `${data?.count ?? 0} matcher`}
          </div>
          {classification === "playoff" && (
            <Badge variant="secondary">Fokus: slutspelsmatcher</Badge>
          )}
        </div>

        <div className="space-y-4">
          {items.map((item) => {
            const draft = drafts[item.id];
            const nextStageType = draft?.stage_type !== undefined ? draft.stage_type : (item.stage_type || "__none__");
            const nextStageGroup = getStageGroupValue(item, draft);
            const nextLeg = getLegValue(item, draft);
            const stageInfo = stageBadge(item);
            const hintInfo = hintBadge(item);
            const validationMessage = validateDraft(item, draft);
            const isDirty = Boolean(draft);
            const groups = nextStageType && nextStageType !== "__none__" ? (STAGE_GROUP_OPTIONS[nextStageType] || []) : [];

            return (
              <Card key={item.id}>
                <CardContent className="grid gap-4 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={stageInfo.variant}>{stageInfo.label}</Badge>
                        <Badge variant="outline" className={hintInfo.className}>{hintInfo.label}</Badge>
                        {item.stage_group && <Badge variant="outline">{item.stage_group}</Badge>}
                        {item.leg != null && <Badge variant="outline">Möte {item.leg}</Badge>}
                        {item.used && <Badge>Skapad i appen</Badge>}
                      </div>
                      <div>
                        <h3 className="text-base font-semibold">
                          {item.home_team || "Hemmalag"} - {item.away_team || "Bortalag"}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {item.date ? formatSvDateTime(item.date, { dateStyle: "medium", timeStyle: "short" }) : "Saknar datum"}
                          {item.id ? ` • ${item.id}` : ""}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Lagindex: hemma #{item.home_index ?? "-"} • borta #{item.away_index ?? "-"}
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Resultat</div>
                      <div className="text-2xl font-semibold">
                        {item.home_score ?? "-"} - {item.away_score ?? "-"}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-[220px_180px_140px_auto]">
                    <Select value={nextStageType} onValueChange={(value) => handleStageTypeChange(item.id, value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Välj stage_type" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border border-border shadow-lg">
                        {STAGE_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={nextStageGroup}
                      onValueChange={(value) => handleDraftChange(item.id, { stage_group: value })}
                      disabled={!groups.length}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Block" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border border-border shadow-lg">
                        <SelectItem value="__none__">Inget block</SelectItem>
                        {groups.map((group) => (
                          <SelectItem key={`${item.id}-${group}`} value={group}>{group}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={nextLeg}
                      onValueChange={(value) => handleDraftChange(item.id, { leg: value })}
                      disabled={!groups.length}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Möte" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border border-border shadow-lg">
                        <SelectItem value="__none__">Inget möte</SelectItem>
                        <SelectItem value="1">Möte 1</SelectItem>
                        <SelectItem value="2">Möte 2</SelectItem>
                      </SelectContent>
                    </Select>

                    <div className="flex items-center justify-end">
                      <Button
                        type="button"
                        onClick={() => handleSaveRow(item)}
                        disabled={saveMutation.isPending || !isDirty}
                      >
                        <Save className="mr-2 h-4 w-4" />
                        Spara
                      </Button>
                    </div>
                  </div>

                  {(item.classification_hint === "playoff_candidate" || item.classification_hint === "uncertain") && (
                    <div className="space-y-2">
                      <div className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                        Snabbval för slutspel
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {QUICK_ACTIONS.map(([label, patch]) => (
                          <Button
                            key={`${item.id}-${label}`}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => applyQuickAction(item.id, patch)}
                          >
                            {label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {validationMessage ? (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{validationMessage}</span>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}

          {!isLoading && !items.length && (
            <Card>
              <CardContent className="p-6 text-sm text-muted-foreground">
                Inga matcher matchade filtren.
              </CardContent>
            </Card>
          )}
        </div>
          </>
        )}
      </main>
    </div>
  );
}
